import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles, Clock, Home, Star, ArrowRight, CheckCircle2, Building2,
  Shield, Award, Timer, Users, Phone, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const LOGO = "https://customer-assets.emergentagent.com/job_09ef4963-c96f-46c6-a08d-052afb978ee9/artifacts/j66rn420_Gleam%20Guru%20Cleaning%20%5BLogo%20Design%5D%20No%20Gleam.png";

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const SERVICES_DATA = {
  'maintenance-clean-portland': {
    name: 'Maintenance Clean Portland',
    headline: 'Weekly & Biweekly Maintenance Cleaning',
    sub: 'Keep your Portland home consistently spotless with our recurring maintenance service.',
    description: 'Our Maintenance Clean is designed for Portland homeowners who want a consistently clean home without the hassle. We handle the regular upkeep — dusting, vacuuming, mopping, kitchen and bathroom sanitizing — so you can focus on what matters most. Available weekly or biweekly to match your lifestyle.',
    features: ['Dust all surfaces and fixtures', 'Vacuum and mop all floors', 'Kitchen counters, appliances, and sink', 'Bathroom sanitizing and scrub', 'Trash removal and spot cleaning', 'Consistent team for every visit'],
    time: '2-3 hours', icon: Clock, serviceType: 'weekly', category: 'Residential',
  },
  'signature-clean-portland': {
    name: 'Signature Clean Portland',
    headline: 'Elevated Detail Cleaning Service',
    sub: 'Our premium recurring clean for homeowners who expect more.',
    description: 'The Signature Clean goes beyond standard maintenance. It delivers an elevated level of detail and care for Portland homeowners who value a pristine home environment. Every visit includes baseboards, light switches, detailed kitchen work, and the kind of thoroughness that makes a visible difference.',
    features: ['Everything in Maintenance Clean', 'Baseboards and door frames wiped', 'Light switch and handle sanitizing', 'Detailed kitchen appliance exteriors', 'Mirror and glass streak-free polish', 'Interior window sill dusting'],
    time: '3-4 hours', icon: Star, serviceType: 'biweekly', category: 'Residential',
  },
  'deep-reset-portland': {
    name: 'Deep Reset Portland',
    headline: 'Complete Deep Cleaning Transformation',
    sub: 'Our signature top-to-bottom deep clean that sets the standard for every visit after.',
    description: 'The Signature Deep Reset is our most thorough cleaning service and the foundation of the Gleam Guru experience. Required for all new clients, this deep clean tackles every surface, corner, and fixture in your home — establishing a pristine baseline that our recurring maintenance services build upon.',
    features: ['Every room cleaned top to bottom', 'Inside cabinets and drawers wiped', 'Behind and under appliances', 'Grout and tile deep scrub', 'Baseboard and trim detail work', 'All fixtures polished and sanitized'],
    time: '4-6 hours', icon: Sparkles, serviceType: 'deep_reset', badge: 'Required for New Clients', category: 'Residential',
  },
  'move-out-move-in-clean-portland': {
    name: 'Move-Out / Move-In Clean Portland',
    headline: 'Professional Move Cleaning Service',
    sub: 'Every surface, cabinet, and closet — immaculate for move day.',
    description: 'Whether you are moving in or out, our Move Clean ensures every inch of the property is spotless. From inside cabinets and closets to appliance interiors and bathroom fixtures, we leave the space pristine for the next chapter. Trusted by Portland realtors and property managers.',
    features: ['Full cabinet and closet interior cleaning', 'Appliance interior deep clean', 'All surfaces sanitized', 'Window sills and tracks cleaned', 'Light fixtures dusted', 'Garage sweep available'],
    time: '5-7 hours', icon: Home, serviceType: 'deep_reset', category: 'Residential',
  },
  'carpet-cleaning-portland': {
    name: 'Carpet Cleaning Portland',
    headline: 'Professional Carpet Deep Cleaning',
    sub: 'Restore your carpets to like-new condition with professional treatment.',
    description: 'Our professional carpet cleaning service tackles deep-set dirt, stains, and allergens that regular vacuuming cannot reach. Using professional-grade equipment and eco-friendly solutions, we restore your carpets and extend their lifespan. Available as a standalone service or add-on.',
    features: ['Pre-treatment of high-traffic areas', 'Professional hot water extraction', 'Stain treatment and removal', 'Deodorizing treatment', 'Fast-dry technology', 'Eco-friendly cleaning solutions'],
    time: '2-4 hours', icon: Sparkles, serviceType: 'deep_reset', category: 'Specialty',
  },
  'airbnb-turnover-cleaning-portland': {
    name: 'Airbnb Turnover Cleaning Portland',
    headline: 'Short-Term Rental Turnover Service',
    sub: 'Guest-ready cleaning for Airbnb and vacation rental hosts.',
    description: 'Maximize your reviews and guest satisfaction with our Airbnb Turnover cleaning. We ensure your rental is guest-ready with fresh linens, restocked supplies, and a spotless environment. Reliable scheduling synced to your booking calendar for worry-free hosting.',
    features: ['Full property cleaning between guests', 'Linen change and bed making', 'Kitchen restocked and sanitized', 'Bathroom deep clean', 'Trash removal and staging', 'Photo-ready final walk-through'],
    time: '2-3 hours', icon: Building2, serviceType: 'airbnb', category: 'Commercial',
  },
  'office-cleaning-portland': {
    name: 'Office Cleaning Portland',
    headline: 'Professional Office & Workspace Cleaning',
    sub: 'Maintain a clean, productive workspace for your Portland team.',
    description: 'Our commercial office cleaning service provides reliable, professional cleaning for Portland businesses. From daily maintenance to deep cleaning, we help maintain a healthy and impressive workspace that reflects well on your business.',
    features: ['Workstation and desk sanitizing', 'Common area cleaning', 'Restroom deep clean and restock', 'Kitchen and break room cleaning', 'Floor care and vacuuming', 'Trash removal and recycling'],
    time: 'Custom', icon: Building2, serviceType: 'deep_reset', category: 'Commercial',
  },
  'trash-out-property-cleanout-portland': {
    name: 'Trash-Out & Property Cleanout Portland',
    headline: 'Property Cleanout & Restoration Service',
    sub: 'Complete property cleanout for abandoned, foreclosed, or post-tenant properties.',
    description: 'Our Trash-Out service handles the complete cleanout of properties left in disarray. Whether it is an abandoned property, post-eviction clean-up, or estate cleanout, we handle debris removal, deep cleaning, and property restoration to get the space ready for the next occupant.',
    features: ['Full debris and junk removal', 'Deep cleaning of all surfaces', 'Appliance cleaning or removal', 'Floor restoration', 'Yard and exterior cleanup', 'Property restoration report'],
    time: '6-10 hours', icon: Home, serviceType: 'deep_reset', category: 'Commercial',
  },
};

const RELATED = [
  { slug: 'maintenance-clean-portland', name: 'Maintenance Clean' },
  { slug: 'deep-reset-portland', name: 'Deep Reset' },
  { slug: 'move-out-move-in-clean-portland', name: 'Move-Out Clean' },
  { slug: 'carpet-cleaning-portland', name: 'Carpet Cleaning' },
];

export default function ServiceDetailPage({ slug: slugOverride }) {
  const { slug: paramSlug } = useParams();
  const slug = slugOverride || paramSlug;
  const service = SERVICES_DATA[slug];
  const relatedServices = useMemo(
    () => RELATED.filter(r => r.slug !== slug).slice(0, 4),
    [slug]
  );

  if (!service) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-white">
        <div className="text-center px-6">
          <h1 className="text-4xl font-bold text-zinc-900 font-heading mb-3">Service Not Found</h1>
          <p className="text-zinc-500 mb-6">We couldn't find that service page.</p>
          <Button asChild className="bg-[#F56F05] hover:bg-[#C2410C] text-white font-bold px-6 h-11 rounded-xl">
            <Link to="/services">View All Services</Link>
          </Button>
        </div>
      </div>
    );
  }

  const Icon = service.icon;

  return (
    <div className="min-h-screen pt-24 bg-white" data-testid="service-detail-page">

      {/* ── Hero ─────────────────────────────────── */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-zinc-950" data-testid="seo-hero">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle, #F56F05 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-sm text-zinc-500 mb-8">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/services" className="hover:text-white transition-colors">Services</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-[#F56F05] font-medium">{service.category}</span>
          </nav>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            {service.badge && (
              <Badge className="bg-[#F56F05]/15 text-[#F56F05] border-[#F56F05]/30 text-xs font-semibold mb-4 px-3 py-1">{service.badge}</Badge>
            )}
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white font-heading mb-4 tracking-tight">
              {service.headline}
            </h1>
            <p className="text-base lg:text-lg text-zinc-400 leading-relaxed mb-6">{service.sub}</p>
            <div className="flex flex-wrap items-center gap-4">
              <Button asChild className="bg-[#F56F05] hover:bg-[#C2410C] text-white font-bold px-8 h-12 rounded-full shadow-lg shadow-orange-500/25">
                <Link to={`/booking?service=${service.serviceType}`}>Book This Service <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <div className="flex items-center gap-1.5 text-sm text-zinc-400">
                <Timer className="h-4 w-4 text-[#F56F05]" />
                <span>Est. {service.time}</span>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" className="w-full"><path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H0Z" fill="white"/></svg>
        </div>
      </section>

      {/* ── Description + Features ────────────────── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-start">
          <motion.div {...fadeUp} className="space-y-5">
            <p className="text-sm font-semibold text-[#F56F05] uppercase tracking-widest">About This Service</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 font-heading">{service.headline}</h2>
            <p className="text-zinc-600 leading-relaxed">{service.description}</p>

            <div className="pt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { icon: Timer, text: `Est. ${service.time}` },
                { icon: Shield, text: 'Background-checked' },
                { icon: Award, text: 'Satisfaction guarantee' },
              ].map(t => (
                <div key={t.text} className="flex items-center gap-2 text-sm text-zinc-600 bg-zinc-50 rounded-xl px-3 py-2.5">
                  <t.icon className="h-4 w-4 text-[#F56F05] flex-shrink-0" /> {t.text}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeUp} transition={{ delay: 0.1 }}>
            <div className="bg-zinc-50 rounded-2xl p-6 border border-zinc-200">
              <div className="w-12 h-12 rounded-xl bg-[#F56F05] flex items-center justify-center mb-5 shadow-lg shadow-[#F56F05]/20">
                <Icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-zinc-900 font-heading mb-4">What's Included</h3>
              <ul className="space-y-3">
                {service.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-[#F56F05] mt-0.5 flex-shrink-0" />
                    <span className="text-zinc-700">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Booking Banner ────────────────────────── */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#F56F05] via-[#F56F05] to-[#C2410C]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeUp} className="space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-white font-heading">
              Ready to Book Your {service.category === 'Commercial' ? 'Consultation' : 'Clean'}?
            </h2>
            <p className="text-lg text-white/90 max-w-xl mx-auto">
              Experience the Gleam Guru difference. Professional, reliable, detail-focused cleaning for your Portland home.
            </p>
            <Button asChild className="bg-white text-[#F56F05] hover:bg-zinc-100 font-bold px-10 py-7 rounded-full shadow-xl text-lg">
              <Link to={`/booking?service=${service.serviceType}`}>
                {service.category === 'Commercial' ? 'Schedule Consultation' : 'Book Now'} <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ── Related Services ─────────────────────── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-10">
            <p className="text-sm font-semibold text-[#F56F05] uppercase tracking-widest mb-3">Explore More</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 font-heading">Other Services You May Need</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {relatedServices.map((r, i) => {
              const rd = SERVICES_DATA[r.slug];
              const RIcon = rd?.icon || Sparkles;
              return (
                <motion.div key={r.slug} {...fadeUp} transition={{ delay: i * 0.06 }}>
                  <Link to={`/services/${r.slug}`}
                    className="group block p-5 rounded-2xl bg-white border-2 border-zinc-100 hover:border-[#F56F05]/40 hover:shadow-lg hover:shadow-orange-50 transition-all h-full">
                    <div className="w-10 h-10 rounded-xl bg-zinc-100 text-zinc-400 group-hover:bg-[#F56F05] group-hover:text-white flex items-center justify-center mb-3 transition-colors">
                      <RIcon className="h-5 w-5" />
                    </div>
                    <h3 className="font-bold text-zinc-900 mb-1 text-sm">{r.name}</h3>
                    <p className="text-xs text-zinc-500">{rd?.sub?.slice(0, 60)}...</p>
                    <span className="inline-flex items-center text-[#F56F05] text-xs font-semibold mt-3 group-hover:gap-1 transition-all">
                      Learn more <ArrowRight className="h-3 w-3 ml-0.5" />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Trust CTA ────────────────────────────── */}
      <section className="py-16 md:py-20 bg-zinc-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <img src={LOGO} alt="Gleam Guru" className="h-14 w-auto mx-auto mb-5" />
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 font-heading tracking-tight mb-3">
              Portland's Trusted Cleaning Service
            </h2>
            <div className="flex flex-wrap justify-center gap-5 mt-5">
              {[
                { icon: Shield, text: 'Background-checked cleaners' },
                { icon: Award, text: 'Satisfaction guarantee' },
                { icon: Users, text: 'Trusted by Portland homeowners' },
              ].map(t => (
                <div key={t.text} className="flex items-center gap-1.5 text-xs text-zinc-500">
                  <t.icon className="h-3.5 w-3.5 text-[#F56F05]" /> {t.text}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
